import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  aurora,
  CompassStar,
  duration,
  easing,
  GlowCard,
  ice,
  Logo,
  midnight,
  SectionHeader,
  semantic,
  StatTile,
  StatusPill,
  typeScale,
} from '@norvix/ui';
import type { ReactNode } from 'react';

function Panel({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ mb: 6 }}>
      <SectionHeader eyebrow={eyebrow} title={title} />
      {children}
    </Box>
  );
}

function Swatch({ name, value }: { name: string; value: string }): JSX.Element {
  return (
    <Stack spacing={0.75} sx={{ minWidth: 120 }}>
      <Box sx={{ height: 64, borderRadius: 'var(--radius-lg)', background: value, border: '1px solid var(--hairline)' }} />
      <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>{name}</Typography>
      <Typography variant="caption" sx={{ color: 'text.muted', fontFamily: 'var(--font-mono)' }}>{value}</Typography>
    </Stack>
  );
}

function swatches(obj: Record<string | number, string>, prefix: string): JSX.Element[] {
  return Object.entries(obj).map(([k, v]) => <Swatch key={`${prefix}-${k}`} name={`${prefix}-${k}`} value={v} />);
}

const TYPE_SAMPLE: Record<string, string> = {
  h1: 'Where money meets trust',
  h2: 'Where money meets trust',
  h3: 'Where money meets trust',
  h4: 'Where money meets trust',
  'body-l': 'Multi-rail payments — SEPA, SCT Inst, SWIFT, cards.',
  'body-m': 'Multi-rail payments — SEPA, SCT Inst, SWIFT, cards.',
  'body-s': 'Multi-rail payments — SEPA, SCT Inst, SWIFT, cards.',
  caption: 'Last updated 2 minutes ago',
  overline: 'Compliance',
};

export function StyleguidePage(): JSX.Element {
  return (
    <Box>
      <SectionHeader
        eyebrow="Design System"
        title="Norvix Aurora v2"
        description="Live reference for the brand, tokens and components — mirrored 1:1 from the Figma “Norvix — Web 2.0” style guide. Use these instead of hardcoding colours, type or the logo."
      />

      {/* ---- Brand / logo ---- */}
      <Panel eyebrow="Brand" title="Logo">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <GlowCard accent="aurora">
              <Stack spacing={4} alignItems="flex-start">
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 1.5 }}>Primary lockup</Typography>
                  <Logo height={56} />
                </Box>
                <Stack direction="row" spacing={4} alignItems="flex-end" flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 1.5 }}>Mark · crystal</Typography>
                    <CompassStar size={72} render="crystal" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 1.5 }}>Mark · gradient</Typography>
                    <CompassStar size={72} render="gradient" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 1.5 }}>Mark · mono</Typography>
                    <CompassStar size={72} render="mono" color="var(--ice-100)" />
                  </Box>
                </Stack>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 1.5 }}>Wordmark</Typography>
                  <Logo variant="wordmark" height={28} />
                </Box>
              </Stack>
            </GlowCard>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <GlowCard>
              <Stack spacing={2}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Lockup at UI sizes</Typography>
                <Stack spacing={2.5}>
                  {[20, 28, 40].map((h) => (
                    <Stack key={h} direction="row" spacing={2} alignItems="center">
                      <Typography variant="caption" sx={{ color: 'text.muted', width: 40, fontFamily: 'var(--font-mono)' }}>{h}px</Typography>
                      <Logo height={h} />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </GlowCard>
          </Grid>
        </Grid>
      </Panel>

      {/* ---- Colour ---- */}
      <Panel eyebrow="Foundations" title="Colour palette">
        <GlowCard>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>{swatches(aurora, 'aurora')}</Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>{swatches(midnight, 'midnight')}</Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>{swatches(ice, 'ice')}</Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>{swatches(semantic, 'semantic')}</Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Swatch name="gradient-aurora" value="var(--gradient-aurora)" />
              <Swatch name="gradient-midnight" value="var(--gradient-midnight)" />
            </Stack>
          </Stack>
        </GlowCard>
      </Panel>

      {/* ---- Typography ---- */}
      <Panel eyebrow="Foundations" title="Typography">
        <GlowCard>
          <Stack spacing={2.5} divider={<Box sx={{ borderBottom: '1px solid var(--hairline)' }} />}>
            {Object.entries(typeScale).map(([key, t]) => (
              <Stack key={key} direction="row" spacing={3} alignItems="baseline">
                <Typography variant="caption" sx={{ color: 'text.muted', width: 72, flexShrink: 0, fontFamily: 'var(--font-mono)' }}>{key}</Typography>
                <span
                  style={{
                    fontFamily: t.fontFamily,
                    fontSize: t.fontSize,
                    fontWeight: t.fontWeight,
                    letterSpacing: t.letterSpacing,
                    lineHeight: String(t.lineHeight),
                    textTransform: t.textTransform ?? 'none',
                    color: 'var(--text-primary)',
                  }}
                >
                  {TYPE_SAMPLE[key] ?? 'Norvix'}
                </span>
              </Stack>
            ))}
            <Stack direction="row" spacing={3} alignItems="baseline">
              <Typography variant="caption" sx={{ color: 'text.muted', width: 72, flexShrink: 0, fontFamily: 'var(--font-mono)' }}>mono</Typography>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, color: 'var(--text-primary)' }}>4921 0000 0000 7034</span>
            </Stack>
          </Stack>
        </GlowCard>
      </Panel>

      {/* ---- Buttons ---- */}
      <Panel eyebrow="Components" title="Buttons & inputs">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <GlowCard>
              <Stack spacing={2.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Buttons</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Button variant="contained">Primary</Button>
                  <Button variant="outlined">Secondary</Button>
                  <Button variant="text">Ghost</Button>
                  <Button variant="contained" disabled>Disabled</Button>
                </Stack>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" size="large">Large primary</Button>
                  <Button variant="outlined" size="small">Small secondary</Button>
                </Stack>
              </Stack>
            </GlowCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <GlowCard>
              <Stack spacing={2.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Inputs</Typography>
                <TextField label="Default" placeholder="you@norvix.eu" fullWidth />
                <TextField label="Focused (click in)" placeholder="Aurora focus ring" fullWidth />
                <TextField label="Error" defaultValue="bad-iban" error helperText="Invalid IBAN" fullWidth />
              </Stack>
            </GlowCard>
          </Grid>
        </Grid>
      </Panel>

      {/* ---- Chips ---- */}
      <Panel eyebrow="Components" title="Status pills & chips">
        <GlowCard>
          <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <StatusPill tone="success">Settled</StatusPill>
              <StatusPill tone="warning">In review</StatusPill>
              <StatusPill tone="error">Blocked</StatusPill>
              <StatusPill tone="info">Processing</StatusPill>
              <StatusPill tone="neutral">Pending</StatusPill>
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Chip label="Success" color="success" variant="outlined" size="small" />
              <Chip label="Warning" color="warning" variant="outlined" size="small" />
              <Chip label="Error" color="error" variant="outlined" size="small" />
              <Chip label="Primary" color="primary" variant="outlined" size="small" />
            </Stack>
          </Stack>
        </GlowCard>
      </Panel>

      {/* ---- Cards ---- */}
      <Panel eyebrow="Components" title="Cards & surfaces">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile label="Settled today" value="€ 1,284,500.00" delta={{ value: '12.5% vs last month', direction: 'up' }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile label="Open AML alerts" value="3" delta={{ value: '2 since yesterday', direction: 'down' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <GlowCard accent="aurora">
              <Stack spacing={2.5}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--aurora-300)' }}>VIRTUAL CARD</Typography>
                <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 22, letterSpacing: '0.02em', color: 'var(--text-primary)' }}>4921 •••• •••• 7034</Typography>
                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>Cardholder</Typography>
                    <Typography variant="body2">A. NORDQVIST</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>Expires</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>09 / 28</Typography>
                  </Box>
                  <Logo variant="mark" height={36} />
                </Stack>
              </Stack>
            </GlowCard>
          </Grid>
        </Grid>
      </Panel>

      {/* ---- Motion ---- */}
      <Panel eyebrow="Foundations" title="Motion">
        <GlowCard>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Durations</Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {Object.entries(duration).map(([k, v]) => (
                  <Stack key={k} direction="row" justifyContent="space-between">
                    <Typography variant="body2">{k}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)', color: 'text.secondary' }}>{v}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Easings</Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {Object.entries(easing).map(([k, v]) => (
                  <Stack key={k} direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2">{k}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)', color: 'text.muted', textAlign: 'right' }}>{v}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </GlowCard>
      </Panel>
    </Box>
  );
}
